import { useEffect, useState, useCallback } from "react";
import { Users, MapPin, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const MAP_W = 520, MAP_H = 360;

export function VenueLiveMap({ venue, onEnter }) {
  const [zones, setZones] = useState([]);
  const [active, setActive] = useState([]);

  const load = useCallback(async () => {
    const headers = { "X-Venue-Id": venue.id };
    try {
      const [z, a] = await Promise.all([
        api.get("/plus/zones", { headers }),
        api.get("/plus/sessions/active", { headers }),
      ]);
      setZones(z.data || []);
      setActive(a.data || []);
    } catch { /* noop */ }
  }, [venue.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const zoneUsers = {};
  active.forEach((s) => { if (s.zone_current) (zoneUsers[s.zone_current] = zoneUsers[s.zone_current] || []).push(s); });

  return (
    <button
      data-testid={`cockpit-venue-${venue.id}`}
      onClick={() => onEnter(venue.id)}
      className="group text-left bg-zinc-900 border border-zinc-800 rounded-2xl p-4 hover:border-sky-500/60 transition-colors flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-sky-400" />
        <h3 className="font-display font-bold text-base truncate flex-1">{venue.nome}</h3>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
          </span>
          <span className="text-[10px] font-bold tracking-wider text-red-300">LIVE</span>
        </span>
      </div>

      {zones.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-zinc-600 text-xs py-10">Nessuna zona configurata</div>
      ) : (
        <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="w-full h-auto rounded-xl bg-zinc-950 border border-zinc-800">
          <defs>
            <pattern id={`grid-${venue.id}`} width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M20 0 L0 0 0 20" fill="none" stroke="#1f2430" strokeWidth="1" />
            </pattern>
          </defs>
          <rect x="0" y="0" width={MAP_W} height={MAP_H} fill={`url(#grid-${venue.id})`} />
          {zones.map((z) => {
            const zu = zoneUsers[z.code] || [];
            const n = zu.length;
            return (
              <g key={z.id}>
                <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="10"
                  fill={`${z.colore}${n > 0 ? "33" : "18"}`} stroke={z.colore} strokeWidth={n > 0 ? "2.5" : "1.5"} />
                <text x={z.x + 12} y={z.y + 24} fill={z.colore} fontSize="13" fontWeight="700">{z.nome}</text>
                {zu.slice(0, 5).map((s, i) => (
                  <g key={s.id}>
                    <circle cx={z.x + 17} cy={z.y + 42 + i * 17} r="4" fill={s.customer?.cv_color || "#a1a1aa"} />
                    <text x={z.x + 27} y={z.y + 46 + i * 17} fontSize="11" fill="#e4e4e7">
                      {`${s.customer?.nome || ""} ${s.customer?.cognome || ""}`.trim().slice(0, 20)}
                    </text>
                  </g>
                ))}
                {n > 5 && <text x={z.x + 17} y={z.y + 46 + 5 * 17} fontSize="10" fill="#a1a1aa">+{n - 5} altri</text>}
                {n > 0 && (
                  <>
                    <circle cx={z.x + z.w - 20} cy={z.y + 20} r="13" fill="#22c55e" />
                    <text x={z.x + z.w - 20} y={z.y + 25} fill="#052e16" fontSize="13" fontWeight="800" textAnchor="middle">{n}</text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      )}

      <div className="flex items-center justify-between mt-3">
        <span className="flex items-center gap-1.5 text-green-400 font-semibold text-sm">
          <Users className="w-4 h-4" />
          <span data-testid={`cockpit-present-${venue.id}`}>{active.length}</span>
          <span className="text-xs text-zinc-500 font-normal">in sala</span>
        </span>
        <span className="flex items-center gap-1 text-sky-400 text-sm opacity-70 group-hover:opacity-100">
          Entra <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </button>
  );
}
